import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import NotFound from "@/pages/NotFound";
import { api } from "@/lib/auth";

export default function BlogPost() {
    const { slug } = useParams();
    const location = useLocation();
    const preview = new URLSearchParams(location.search).get("preview") === "1";
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (typeof window !== "undefined") window.prerenderReady = false;
        setLoading(true);
        setNotFound(false);
        api
            .get(`/public/blog/posts/${slug}`, { params: preview ? { preview: 1 } : {} })
            .then((r) => setPost(r.data))
            .catch((err) => {
                if (err?.response?.status === 404) setNotFound(true);
            })
            .finally(() => {
                setLoading(false);
                requestAnimationFrame(() => {
                    if (typeof window !== "undefined") window.prerenderReady = true;
                });
            });
    }, [slug, preview]);

    if (notFound) return <NotFound />;

    if (loading || !post) {
        return (
            <div className="py-24 text-center text-sm text-mm-muted" data-testid="blog-post-loading">
                Loading...
            </div>
        );
    }

    const brandPhone = post.brand?.phone || "(813) 739-9877";
    const brandPhoneTel = post.brand?.phone_tel || "+18137399877";

    const jsonLdArray = [post.article_ld, post.breadcrumb_ld, post.faq_ld].filter(Boolean);

    return (
        <article className="bg-mm-bg/40" data-testid="blog-post-page">
            <SEO
                title={post.meta_title || post.title}
                description={post.meta_description}
                canonical={post.canonical}
                ogImage={post.hero_image_url}
                ogType="article"
            />
            {jsonLdArray.map((ld, i) => (
                <JsonLdScript key={i} id={`blog-ld-${i}`} data={ld} />
            ))}

            {post.is_preview && (
                <div
                    className="bg-amber-100 py-2 text-center text-sm text-amber-900"
                    data-testid="blog-preview-banner"
                >
                    Preview mode — this post is not yet live.
                </div>
            )}

            <section className="border-b border-mm-line bg-gradient-to-b from-mm-bg to-white py-12 sm:py-16">
                <div className="mm-container">
                    <div className="text-[0.72rem] uppercase tracking-wider text-mm-blue">
                        {post.category}
                    </div>
                    <h1
                        className="mt-3 max-w-3xl font-serif text-3xl text-mm-navy sm:text-5xl"
                        data-testid="blog-post-title"
                    >
                        {post.title}
                    </h1>
                    <div className="mt-4 text-sm text-mm-muted">
                        By <strong>{post.author.name}</strong>
                        {post.published_at && (
                            <>
                                {" "}
                                ·{" "}
                                <time dateTime={post.published_at}>
                                    {post.published_at.slice(0, 10)}
                                </time>
                            </>
                        )}
                        {post.updated_at && post.updated_at !== post.published_at && (
                            <>
                                {" "}
                                · Updated{" "}
                                <time dateTime={post.updated_at}>
                                    {post.updated_at.slice(0, 10)}
                                </time>
                            </>
                        )}
                    </div>
                    {post.hero_image_url && (
                        <img
                            src={post.hero_image_url}
                            alt={post.hero_image_alt}
                            className="mt-8 max-h-[420px] w-full rounded-2xl border border-mm-line object-cover"
                            data-testid="blog-post-hero-img"
                        />
                    )}
                </div>
            </section>

            <div className="mm-container py-10">
                <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)]">
                    {post.toc && post.toc.length > 0 ? (
                        <aside
                            className="hidden self-start rounded-2xl border border-mm-line bg-white p-5 text-sm lg:sticky lg:top-24 lg:block"
                            aria-label="On this page"
                            data-testid="blog-post-toc"
                        >
                            <h4 className="mb-2 text-[0.72rem] uppercase tracking-wider text-mm-muted">
                                On this page
                            </h4>
                            <ul className="space-y-1">
                                {post.toc.map((t) => (
                                    <li key={t.anchor}>
                                        <a
                                            href={`#${t.anchor}`}
                                            className="block border-l-2 border-transparent py-0.5 pl-2 text-mm-ink hover:border-mm-blue hover:text-mm-blue"
                                        >
                                            {t.text}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </aside>
                    ) : (
                        <div />
                    )}

                    <div className="max-w-3xl">
                        <div
                            className="post-body text-base text-mm-ink"
                            dangerouslySetInnerHTML={{ __html: post.body_html }}
                            data-testid="blog-post-body"
                        />

                        {post.faq && post.faq.length > 0 && (
                            <section className="mt-10" data-testid="blog-post-faq">
                                <h2 className="mb-3 font-serif text-2xl text-mm-navy">
                                    Frequently asked questions
                                </h2>
                                <div className="space-y-2">
                                    {post.faq.map((item, i) => (
                                        <details
                                            key={i}
                                            className="overflow-hidden rounded-xl border border-mm-line bg-white"
                                            data-testid={`faq-item-${i + 1}`}
                                        >
                                            <summary className="flex cursor-pointer items-center justify-between px-5 py-4 font-semibold text-mm-navy">
                                                <span>{item.q}</span>
                                                <span className="text-mm-blue">+</span>
                                            </summary>
                                            <div className="px-5 pb-5 text-sm text-mm-ink">
                                                {item.a}
                                            </div>
                                        </details>
                                    ))}
                                </div>
                            </section>
                        )}

                        <div
                            className="mt-10 flex items-start gap-4 rounded-2xl border border-mm-line bg-white p-5"
                            data-testid="blog-post-author"
                        >
                            <img
                                src={post.author.avatar}
                                alt={`${post.author.name} headshot`}
                                className="h-16 w-16 flex-shrink-0 rounded-full border-2 border-mm-sky object-cover"
                            />
                            <div>
                                <h4 className="text-base font-semibold text-mm-navy">
                                    {post.author.name}
                                </h4>
                                <p className="text-sm text-mm-muted">
                                    {post.author.title}. President of Moving Mountains
                                    Advisors, a boutique independent agency serving
                                    Florida and beyond.
                                </p>
                            </div>
                        </div>

                        <div
                            className="mt-10 grid gap-4 rounded-2xl bg-mm-navy p-8 text-white sm:grid-cols-[1fr_auto] sm:items-center"
                            data-testid="blog-post-cta"
                        >
                            <div>
                                <h3 className="font-serif text-xl text-white">
                                    Want a human to walk you through this?
                                </h3>
                                <p className="mt-2 text-sm text-white/75">
                                    Talk to Jarvis for a plain-English pre-qualification,
                                    book a 20-minute advisor call, or dial us directly.
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Link
                                    to="/jarvis"
                                    className="inline-flex items-center gap-1 rounded-full bg-mm-sky px-4 py-2 text-sm font-semibold text-mm-navy"
                                >
                                    Talk to Jarvis <ArrowRight size={14} />
                                </Link>
                                <Link
                                    to="/book"
                                    className="inline-flex items-center gap-1 rounded-full border border-white/40 px-4 py-2 text-sm font-semibold"
                                >
                                    Book a call
                                </Link>
                                <a
                                    href={`tel:${brandPhoneTel}`}
                                    className="inline-flex items-center gap-1 rounded-full border border-white/40 px-4 py-2 text-sm font-semibold"
                                >
                                    <Phone size={14} /> {brandPhone}
                                </a>
                            </div>
                        </div>

                        {post.disclaimer && (
                            <p
                                className="mt-8 rounded-xl border border-dashed border-mm-line bg-white p-4 text-sm italic text-mm-muted"
                                data-testid="blog-post-disclaimer"
                            >
                                {post.disclaimer}
                            </p>
                        )}
                    </div>
                </div>

                {post.related && post.related.length > 0 && (
                    <section className="mt-16" data-testid="blog-post-related">
                        <h2 className="mb-4 font-serif text-2xl text-mm-navy">
                            Related reading
                        </h2>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {post.related.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/blog/${r.slug}`}
                                    className="overflow-hidden rounded-2xl border border-mm-line bg-white transition-colors hover:border-mm-sky"
                                    data-testid={`related-card-${r.slug}`}
                                >
                                    <div className="aspect-video overflow-hidden bg-mm-bg">
                                        {r.hero_image_url && (
                                            <img
                                                src={r.hero_image_url}
                                                alt={r.hero_image_alt || r.title}
                                                loading="lazy"
                                                className="h-full w-full object-cover"
                                            />
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <span className="text-[0.7rem] uppercase tracking-wider text-mm-blue">
                                            {r.category}
                                        </span>
                                        <h3 className="mt-1 text-sm font-semibold text-mm-navy">
                                            {r.title}
                                        </h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            <style>{`
                .post-body h2 { font-family: Fraunces, Georgia, serif; font-size: 1.5rem; color: #0A2540; margin: 2.25rem 0 0.75rem; scroll-margin-top: 96px; }
                .post-body h3 { font-family: Fraunces, Georgia, serif; font-size: 1.2rem; color: #0A2540; margin: 1.5rem 0 0.5rem; }
                .post-body p { margin: 0 0 1.1rem; font-size: 1.05rem; line-height: 1.65; }
                .post-body ul, .post-body ol { padding-left: 1.25rem; margin: 0 0 1.25rem; }
                .post-body li { margin: 0.4rem 0; }
                .post-body a { color: #2E6AA8; text-decoration: underline; text-decoration-color: rgba(46,106,168,0.3); text-underline-offset: 2px; }
                .post-body blockquote { margin: 1.5rem 0; padding: 0.75rem 1.1rem; border-left: 3px solid #58A6D8; background: #F6F8FB; border-radius: 0 10px 10px 0; color: #5E6B7A; }
                .post-body code { background: #eef2f7; padding: 0.1rem 0.4rem; border-radius: 6px; font-size: 0.95em; }
                .post-body table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; font-size: 0.95rem; }
                .post-body th, .post-body td { padding: 0.6rem 0.8rem; border-bottom: 1px solid #E3E8EF; text-align: left; }
                .post-body th { color: #0A2540; font-weight: 600; }
            `}</style>
        </article>
    );
}

function JsonLdScript({ id, data }) {
    useEffect(() => {
        if (!data) return;
        let el = document.getElementById(id);
        if (!el) {
            el = document.createElement("script");
            el.type = "application/ld+json";
            el.id = id;
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(data);
        return () => {
            const n = document.getElementById(id);
            if (n && n.parentNode) n.parentNode.removeChild(n);
        };
    }, [id, data]);
    return null;
}
